import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";
import type { SprintSummary } from "../../shared/types/api";

/**
 * Cancels a sprint.
 *
 * <p>The panel spells out both consequences before anything happens, because neither is obvious
 * and one of them is the reason to cancel rather than complete: the work goes back to the backlog
 * rather than being lost, and no metrics are recorded, so a called-off sprint does not drag the
 * velocity trend down as though the team had committed and failed.
 */
export function CancelSprintDrawer({
  sprint,
  onClose,
  onSaved,
}: {
  sprint: SprintSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (sprint) {
      setReason("");
      setError(null);
    }
  }, [sprint]);

  const submit = async () => {
    if (!sprint) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/sprints/${sprint.id}/cancel`, { reason: reason || null });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not cancel the sprint.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(sprint)}
      onClose={onClose}
      title="Cancel this sprint"
      subtitle={sprint?.name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={reason.trim() === ""}
          >
            {saving ? "Cancelling…" : "Cancel sprint"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="warning">
          The {sprint?.itemCount ?? 0} items in this sprint go back to the backlog. Nothing is
          deleted, and the sprint itself stays on record.
        </InlineAlert>

        <InlineAlert tone="info">
          No metrics are recorded for a cancelled sprint. It measured nothing, and writing zeroes
          would pull the velocity trend down as though the team had committed and missed.
        </InlineAlert>

        <Field
          label="Why is it being cancelled?"
          required
          hint="Kept on the sprint. Someone reviewing the trend later will want to know why there is a gap."
        >
          <Textarea
            rows={3}
            autoFocus
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="The customer paused the engagement two days in."
          />
        </Field>
      </div>
    </Drawer>
  );
}
